

































































































import { Component, Vue } from "vue-property-decorator";
import { RingLoader } from "vue-spinners-css";

import _auth from "@/utils/auth";
import _gateway from "@/utils/gateway";
import { Prop } from "vue-property-decorator";
import { OTP_RESEND_TIMER_IN_MINUTES } from "@/utils/constants";
import { OTP_LENGTH } from "@/utils/constants";
import { BaseSuccessLoginResult, VerificationMethod, VerifyCodeResponse } from "@/models";
import { LoadingSymbolColor } from "@/views/BaseRootPageComponent";
import Modals from "@/utils/modals";
import * as biometrics from "./biometrics";

@Component({
  components: { RingLoader }
})
export default class OneTimePassword extends Vue {
  // Use a class property for dependencies so unit tests can override them
  auth = _auth;
  gateway = _gateway;

  @Prop() phoneOrEmail!: string;
  @Prop() method!: VerificationMethod;

  isDestroyed = false;

  resendMessage = "";
  error = "";
  otp = "";

  /** Indicates that the user opted to use biometrics on the sign-in page */
  isSettingUpBiometrics = false;

  isTrustedDevice = false;
  otpLen = OTP_LENGTH;
  loading = false;

  /** Only show the resend timer if the user taps on the resend button while a timer is active */
  showResendTimeRemaining = false;
  /**
   * This indicates how long the user must wait before they can resend. By initially leaving this empty, the user
   * can request their first resend without waiting for 3 minutes.
   */
  resendTimeRemaining = "";
  resendTimerIntervalId?: number;

  get showTrustedDeviceCheckbox() {
    // The device will automatically be trusted when setting up biometrics or if the device's trust expired and
    // biometrics are already configured
    return !this.isSettingUpBiometrics && !this.$nativeApp.isUsingBiometrics;
  }

  get loadingSymbolColor() {
    return LoadingSymbolColor;
  }

  mounted() {
    this.isSettingUpBiometrics = this.$route.query["useBiometrics"] === "true";
  }

  beforeDestroy() {
    this.clearResendTimer();
    this.isDestroyed = true;
  }

  async resend() {
    // If the user tapped on the resend button and there's time remaining, show them the timer and don't let them
    // request a new code yet.
    if (this.resendTimeRemaining) {
      this.showResendTimeRemaining = true;
      return;
    }

    this.clearResendTimer();

    this.resendMessage = "";
    this.error = "";

    try {
      await this.gateway.requestVerificationCode(this.method);

      this.resendMessage = "Code resent successfully";
      this.startResendTimer();
    } catch (error) {
      this.error = "An unknown error occurred";
      console.error(error);
    }
  }

  async submitOtp() {
    this.error = "";
    this.resendMessage = "";

    if (this.otp.length !== this.otpLen) {
      this.error = `The verification code should be ${OTP_LENGTH} digits`;
      return;
    }

    try {
      this.loading = true;
      const result = await this.auth.loginWith2FA(this.otp);
      if (!result.isSuccess) {
        if (result.isAccountLocked) {
          await this.handleLockedAccount();
        } else {
          this.error = result.errorMessage || "An unknown error occurred";
        }

        return;
      }

      if (this.isSettingUpBiometrics && !this.$nativeApp.isUsingBiometrics) {
        await this.setupBiometrics();
      }
      // If the user is already configured for biometrics but the trusted device expired, re-trust the device
      else if (this.isTrustedDevice || this.$nativeApp.isUsingBiometrics) {
        try {
          await this.addAsTrustedDevice();
        } catch (error) {
          await Modals.trustedDevice.add.showUnknownError(this);
        }
      }

      await this.checkForExpiringPasswordAndRedirectToHomePage(result);
    } catch (error) {
      this.error = "An unknown error occurred";
      console.error(error);
    } finally {
      this.loading = false;
    }
  }

  async handleLockedAccount() {
    // Logout the user before showing the modal, so that if they refresh the page, their JWT is deleted
    await this.auth.logout();

    // Clear the loading flag so the user can still see the page in the background of the modal
    this.loading = false;

    // Let the user know that they are logged out via a modal and once closed, redirect to the sign-in page
    await Modals.showOk(
      this,
      "Your account is locked. To unlock, click on the Forgot Password link to reset your password or wait for 24 hours.",
      { title: "Locked Out" }
    );

    await this.redirectToSignIn();
  }

  async setupBiometrics() {
    // Even if the setup fails, the user should still continue logging in
    try {
      await this.addAsTrustedDevice();

      await biometrics.setup(this, this.gateway);
    } catch (error) {
      await Modals.biometric.setup.showUnknownError(this);
    }
  }

  async addAsTrustedDevice() {
    const secret = await this.gateway.addTrustedDevice({
      guid: this.auth.deviceGuid,
      type: this.$nativeApp.deviceType,
      os: this.$nativeApp.deviceOS,
      make: this.$nativeApp.deviceMake,
      model: this.$nativeApp.deviceModel
    });

    this.$nativeApp.saveTrustedDeviceSecret(secret);
  }

  async checkForExpiringPasswordAndRedirectToHomePage(result: BaseSuccessLoginResult) {
    const route = (this.$route.query["redirectTo"] || "/") as string;

    if (result.isPasswordExpiringSoon) {
      if (result.daysToPasswordExpiration === 0) {
        await Modals.login.showPasswordExpiringTodayAndNavigateToChangePassword(this, route);
      } else {
        await Modals.login.showPasswordExpiringSoonAndNavigateAway(this, result.daysToPasswordExpiration, route);
      }
    } else {
      this.$router.push(route);
    }
  }

  async redirectToSignIn() {
    await this.auth.logout();
    this.$router.push({
      path: "/sign-in",
      query: { redirectTo: this.$route.query["redirectTo"] }
    });
  }

  startResendTimer() {
    // If the component is already destroyed, no need to start the timer
    if (this.isDestroyed) return;

    this.clearResendTimer();

    const resendDate = new Date(Date.now() + OTP_RESEND_TIMER_IN_MINUTES * 60 * 1000);

    const calculateResendTimeRemaining = () => {
      // If the component is already destroyed, no need to calculate the remaining time
      if (this.isDestroyed) {
        this.clearResendTimer();
        return;
      }

      const remainingSeconds = Math.floor((resendDate.getTime() - Date.now()) / 1000.0);
      if (remainingSeconds <= 0) {
        this.clearResendTimer();
        return;
      }

      const minutes = Math.floor(remainingSeconds / 60);
      const seconds = Math.floor(remainingSeconds % 60);
      this.resendTimeRemaining = minutes + ":" + seconds.toString().padStart(2, "0");
    };

    // Immediately calculate the time remaining and then update the timer every 1 second
    calculateResendTimeRemaining();
    this.resendTimerIntervalId = setInterval(calculateResendTimeRemaining, 1000);
  }

  clearResendTimer() {
    clearInterval(this.resendTimerIntervalId);
    this.resendTimerIntervalId = undefined;
    this.resendTimeRemaining = "";
    this.showResendTimeRemaining = false;
  }

  onOtpUpdate() {
    if (this.otp) {
      this.error = "";
      this.resendMessage = "";
    }
  }
}
